import { Container, Text } from "pixi.js";
import { HoldGridPresenter, HoldSpinMechanic, buildHoldReplayPlugin } from "@slot/mechanics-hold";
import { CompositeGridPresenter } from "@slot/mechanics-core";
import { GridLayer, ScalePulseSymbolWinEffect, WinAmountLabel, WinHighlightEffect, createSymbolSpineEffects } from "@slot/presentation";
import { BootGridPresenter, WinSequencePresenter, createShellRoundPlugins } from "@slot/game-shell";
import type { GamePresentation, PresentationDeps } from "@slot/game-shell";
import { BASE_CELL_COUNT, GRID_CELL_COUNT, GRID_SIZE } from "../gridSize";
import { HoldBonusPresenter } from "../features/bonus/HoldBonusPresenter";
import { ActiveLinesPresenter } from "../features/lines/ActiveLinesPresenter";
import { LineSelectorPresenter } from "../features/lines/LineSelectorPresenter";
import { createHoldBonusPlugin, createPotsPlugin } from "../features/plugins";
import { PotsPresenter } from "../features/pots/PotsPresenter";
import { ShinePresenter } from "../features/shine/ShinePresenter";
import { BLANK } from "../rules/symbols";
import type { CoinHoldScene } from "../scene/createScene";
import { FX_SCOPE } from "../scene/fxScope";
import { CELL, GRID_AREA, ROLL_WINDOW, frameBox } from "../scene/layout";
import { MultiplierBadgesView } from "../scene/MultiplierBadgesView";
import { ShineSweeper } from "../scene/ShineSweeper";
import { CoinGridCodec } from "../symbols/CoinGridCodec";
import { CoinLandEffect } from "../symbols/CoinLandEffect";
import { CoinSymbolView } from "../symbols/CoinSymbolView";
import { BLANK_LIT, BLURRED, OUT_OF_PLAY, SymbolCatalog } from "../symbols/SymbolCatalog";
import { HOLD_MOTION_TUNING, landingProfile } from "../tuning/holdTuning";
import { BIG_WIN_TUNING, FEATURE_FINISHED_TUNING, FEATURE_TRIGGER_TUNING } from "../tuning/panelTuning";
import { WIN_PRESENTATION_TUNING } from "../tuning/winPresentationTuning";
import { FONT } from "../views/textStyles";

/** The base board's cells: the player's spin rolls them before the answer (the GROW rows stay hidden and held). */
const BASE_CELLS = Array.from({ length: BASE_CELL_COUNT }, (_, i) => i);

/** What a rolling cell shows on its way past, blurred along the roll: mostly blanks, plain coins, a jackpot coin, a mystery. */
const FILLER_SYMBOL_IDS: readonly string[] = [
  ...Array.from({ length: 5 }, () => BLURRED.blank),
  ...Array.from({ length: 4 }, () => BLURRED.coin),
  BLURRED.mini,
  BLURRED.minor,
  BLURRED.mystery,
];
/** The same past an active line's cell (decision 56): its blanks the red badge. */
const LIT_FILLER_SYMBOL_IDS: readonly string[] = FILLER_SYMBOL_IDS.map((id) => (id === BLURRED.blank ? BLURRED.blankLit : id));

/** No coin at rest to shine: an empty cell's looks (its badge, blue or lit; a cell out of play) and the rolling fillers. */
const NOT_A_COIN: ReadonlySet<string> = new Set([BLANK, BLANK_LIT, OUT_OF_PLAY, ...Object.values(BLURRED)]);

/** A node's box in `space`'s coordinates, from its bounds on the canvas. */
function boxIn(space: Container, node: Container): { x: number; y: number; width: number; height: number } {
  const bounds = node.getBounds();
  const from = space.toLocal({ x: bounds.minX, y: bounds.minY });
  const to = space.toLocal({ x: bounds.maxX, y: bounds.maxY });
  return { x: from.x, y: from.y, width: to.x - from.x, height: to.y - from.y };
}

/**
 * This game's round presentation (plan §2.2): the grid and the hold mechanic, the pots and the bonus board, the shell's
 * win sequence and the plugin list — everything the shell builds the engine, the runtime and the control bar around.
 * The shared behaviour is the engine's (decision 46); this file names this game's pieces.
 *
 * Mechanic: hold and win (`@slot/mechanics-hold`, decision 50). Every cell is its own reel: the player's spin rolls the
 * base board's twelve cells and lands them row by row, left to right; a respin rolls the cells the server does not hold
 * (`hold_cells`) and lands them one at a time. The coins carry their values in their ids, which their views draw. The
 * game's features are the pots (gems, boosters) and the bonus board (the meter, GROW, the collects, MULTI's badges, the
 * mysteries, the board paying); the game pays no line, so the shell's win sequence stands by for a
 * `grid_win_highlight` the server never sends.
 */
export function createPresentation(scene: CoinHoldScene, deps: PresentationDeps): GamePresentation {
  const { game, gameModals, assets } = scene;
  const s = deps.services;
  const log = s.log;
  const runner = s.presentationTaskRunner;
  const format = (minor: string): string => s.moneyContext.formatter.formatMinor(minor, { omitSymbol: true });

  // The symbol effects' wiring (decision 46). The coins are sprites: no Spine host, so the dim and the grid's bind and
  // release hooks are what remains of it.
  const symbolEffects = createSymbolSpineEffects({ sources: new Map(), runner, driver: deps.spineDriver, clips: { idle: null, land: null, win: null, out: null } });

  // ── The grid and the hold mechanic ──────────────────────────────────────────────────────────────────────────────
  const gridLayer = new GridLayer(
    {
      gridRoot: game.grid.gridRoot,
      layoutRoot: game.grid.gridArea,
      layoutArea: GRID_AREA,
      gridColumnCount: deps.grid.columns,
      gridRowCount: deps.grid.rows,
      cellWidth: CELL.pitchX,
      cellHeight: CELL.pitchY,
      autoFitToLayoutRoot: true,
      centerGrid: true,
      columnOffsets: Array.from({ length: deps.grid.columns }, () => 0),
    },
    {
      definitionResolver: new SymbolCatalog(assets.symbols),
      log,
      symbolFactory: () => new CoinSymbolView({ format, log, collectors: game.collectors, shines: game.coinShines }),
      onSymbolBound: symbolEffects.onSymbolBound,
      onSymbolReleased: (view) => {
        symbolEffects.onSymbolReleased(view);
        // A coin's shine and a collect coin's collector go back with their cell.
        if (view instanceof CoinSymbolView) view.releaseArt();
      },
    },
  );

  // The shine on the gold (the user, 2026-10-06): glints across the frames, the plaques, the pots and the coins, sparkles
  // on their rims — soft by turns while the game waits, bright on the round's moments.
  const shine = new ShinePresenter({
    sweeper: new ShineSweeper(),
    targets: {
      frame: () => (game.grid.frameNode ? { node: game.grid.frameNode, rim: frameBox(game.grid.rows) } : null),
      rowFrame: () => {
        const node = game.grid.rowFrameNode;
        return node ? { node, rim: boxIn(game.fx, node) } : null;
      },
      plaque: (spot) => ({ node: game.plaques.node(spot), rim: game.plaques.rim(spot) }),
      pot: (kind) => ({ node: game.pots.node(kind), rim: game.pots.rim(kind) }),
      coins: () => {
        const coins: Container[] = [];
        for (let index = 0; index < GRID_CELL_COUNT; index++) {
          const view = gridLayer.getSymbolViewAtIndex(index);
          const id = view?.getSymbolId();
          if (view && id && !NOT_A_COIN.has(id)) coins.push(view.contentNode);
        }
        return coins;
      },
    },
    fx: game.fx,
    runner,
    ambientRunner: s.chromeTaskRunner,
    betLines: deps.betLines,
  });

  // The active lines (decision 56): their rows lit and framed in the base game, chosen on the LINES selector.
  const activeLines = new ActiveLinesPresenter({ grid: game.grid, gridLayer, betLines: deps.betLines, onLinesChanged: () => shine.linesChanged() });
  const lineSelector = new LineSelectorPresenter({ view: game.lineSelector, betLines: deps.betLines, canvasTaps: deps.canvasTaps });

  const badges = new MultiplierBadgesView({ cellPosition: (index) => gridLayer.getCellLocalPosition(index), runner });
  game.grid.badgeLayer.addChild(badges);
  const pots = new PotsPresenter({
    view: game.pots,
    fx: game.fx,
    gems: assets.textures.gems,
    cellCentre: (index) => game.grid.cellCentre(index),
    // The pots hold at a line's bet (decision 56).
    bet: deps.betLines,
    onBoosters: (boosters) => gameModals.featureTriggerView.setBoosters(boosters),
    runner,
    log,
    shine,
    coinBursts: game.coinBursts,
  });
  // The jackpot plaques' amounts: multiples of a line's bet (decision 56) — while a bonus plays, of the bet its values follow.
  let jackpotBetMinor: string | null = null;
  const showJackpots = (): void => game.plaques.setBet(BigInt(jackpotBetMinor ?? deps.betLines.getSnapshot().lineBetMinor));
  showJackpots();
  deps.betLines.subscribe(showJackpots);
  const bonus = new HoldBonusPresenter({
    scene: game,
    entry: gameModals.bonusTransition,
    grid: gridLayer,
    badges,
    badgeTexture: assets.textures.multiplierBadge,
    pots,
    format,
    runner,
    log,
    lines: {
      setBonus: (active) => {
        activeLines.setBonus(active);
        lineSelector.setBonus(active);
      },
    },
    setJackpotBet: (betMinor) => {
      jackpotBetMinor = betMinor;
      showJackpots();
    },
    shine,
  });

  const landEffect = new CoinLandEffect(runner, (node) => shine.coinLanded(node));
  const mechanic = new HoldSpinMechanic(gridLayer, {
    taskRunner: runner,
    motionTuning: HOLD_MOTION_TUNING,
    fillerSymbolIds: (index) => (activeLines.isLit(index) ? LIT_FILLER_SYMBOL_IDS : FILLER_SYMBOL_IDS),
    speedModeProvider: () => s.presentationClock.getMode(),
    window: ROLL_WINDOW,
    landEffect,
  });
  // A hidden GROW row's blanks decode as cells out of play (drawn as nothing): the codec reads the rows the frame shows.
  const basePresenter = new HoldGridPresenter({
    mechanic,
    gridCodec: new CoinGridCodec(() => game.grid.rows, (index) => activeLines.isLit(index)),
    presentationBarrier: s.presentationBarrier,
    landingProfile,
    log,
  });

  // ── The win presentation the shell's sequence needs (the game pays no line: it stands by) ──────────────────────
  const winHighlightEffect = new WinHighlightEffect({
    gridView: gridLayer,
    log,
    moneyFormatter: s.moneyContext.formatter,
    createWinAmountLabel: () => {
      const root = new Container({ label: "WinAmountLabel" });
      const label = new Text({ text: "", style: { fontFamily: FONT, fontWeight: "900", fontSize: 80, fill: 0xffffff, stroke: { color: 0x7a1004, width: 12, join: "round" } }, label: "Amount" });
      label.anchor.set(0.5);
      root.addChild(label);
      return new WinAmountLabel({ node: root, label });
    },
    overlayRoot: game.fx,
    timings: WIN_PRESENTATION_TUNING.layer,
    labelTuning: WIN_PRESENTATION_TUNING.label,
    symbolWinEffect: new ScalePulseSymbolWinEffect(() => WIN_PRESENTATION_TUNING.scalePulse, runner, { once: true }),
    exposureOwner: symbolEffects.dim,
    taskRunner: runner,
  });
  const gridPresenter = new CompositeGridPresenter({ base: basePresenter, effects: [winHighlightEffect] });
  const holdPlugin = buildHoldReplayPlugin({ gridPresenter, basePresenter, cellCount: GRID_CELL_COUNT });

  // The board as the wire has it: a cell out of play, or an active line's lit one, is the blank the server sent.
  const wireId = (id: string): string => (id === OUT_OF_PLAY || id === BLANK_LIT ? BLANK : id);
  game.readout = {
    board: () => Array.from({ length: GRID_SIZE.rows }, (_, row) => Array.from({ length: GRID_SIZE.columns }, (_, col) => wireId(gridLayer.getSymbolViewAtIndex(gridLayer.indexOf(row, col))?.getSymbolId() ?? ""))),
    pots: () => pots.levels,
    multipliers: () => bonus.multipliers(),
    litRows: () => activeLines.litRows,
    litBlanks: () => Array.from({ length: BASE_CELL_COUNT }, (_, i) => i).filter((i) => gridLayer.getSymbolViewAtIndex(i)?.getSymbolId() === BLANK_LIT),
    shine: () => ({ glinting: shine.glinting, moments: shine.moments, sparkles: game.fx.children.filter((child) => child.label === "Twinkle").length }),
    paid: () => Array.from({ length: GRID_CELL_COUNT }, (_, i) => i).filter((i) => {
      const view = gridLayer.getSymbolViewAtIndex(i);
      return view instanceof CoinSymbolView && view.paid;
    }),
  };

  const bootGridPresenter = new BootGridPresenter({
    grid: {
      reset: () => {
        shine.newRoll();
        bonus.reset();
        pots.reset(deps.betLines.getSnapshot().lineBetMinor);
        mechanic.resetPresentationState();
      },
      paint: (encoded) => basePresenter.paintGrid(encoded),
    },
    waitForNextRenderedFrame: deps.waitForNextRenderedFrame,
    log,
  });

  const winSequencePresenter = new WinSequencePresenter({
    runner,
    audioService: s.audioService,
    hapticsService: s.hapticsService,
    winHighlightEffect,
    tuning: WIN_PRESENTATION_TUNING,
  });

  // Each respin waits for the player's Spin (decision 55): the gold shines meanwhile.
  const bonusSpins = {
    awaitSpin: async (): Promise<void> => {
      shine.setBonusWaiting(true);
      try {
        await deps.playerSpins.awaitSpin();
      } finally {
        shine.setBonusWaiting(false);
      }
    },
  };

  // ── The replay plugins: the shell's half of the round in its one order, the mechanic, then the pots and the board ─
  const plugins = createShellRoundPlugins({
    services: s,
    lifecycle: deps.lifecycle,
    panels: {
      featureTrigger: gameModals.featureTriggerView,
      featureFinished: gameModals.featureFinishedView,
      bigWin: gameModals.bigWinView,
      gameTransition: gameModals.gameTransitionView,
    },
    tuning: { featureTrigger: FEATURE_TRIGGER_TUNING, featureFinished: FEATURE_FINISHED_TUNING, bigWin: BIG_WIN_TUNING, autoAdvance: WIN_PRESENTATION_TUNING.autoAdvance },
    winSequence: winSequencePresenter,
    scatterHighlightEffect: null,
    beginSpinCycle: () => gridPresenter.clearForNewSpin(),
    mechanic: holdPlugin.plugin,
    features: [createPotsPlugin(pots, runner), createHoldBonusPlugin(bonus, runner, bonusSpins)],
  });

  return {
    framePresenter: holdPlugin.framePresenter,
    plugins,
    bootGridPresenter,
    // The player's spin, before the server answers: the base board's cells start to roll.
    preSpin: async () => {
      shine.newRoll();
      gridPresenter.clearForNewSpin();
      mechanic.startRolling(BASE_CELLS);
      s.presentationBarrier.release("spin");
    },
    // A tap brakes every rolling cell onto the result — its coins landing at rest, without their pop or their glint — and
    // snaps the title's animation playing (a flight, a count); the round's glints come off.
    slam: () => {
      landEffect.slam();
      shine.slam();
      const braked = mechanic.requestSlamStop();
      const snapped = runner.slamFinishScope(FX_SCOPE);
      const win = winSequencePresenter.slamActiveWin();
      return braked || snapped || win;
    },
    slamScopes: holdPlugin.slamScopes,
    gamblePresentation: null,
    // A round holds until the artist's animations are in (decision 71): its pots, comets, eruptions and smoke play as
    // the artist made them, never their stand-ins while they are still on their way (the user, 2026-10-11).
    beforeRound: () => scene.game.whenArtReady(),
  };
}
